import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  Hand,
  ImageIcon,
  SquareMousePointer,
  TypeIcon,
} from "lucide-react";
import FrameOptions from "./components/FrameOptions";
import TypographyOptions from "./components/TypographyOptions";

import { cn } from "@/lib/utils";
import {
  CursorType,
  ModalType,
  setCursorType,
  setModal,
} from "@/store/globalSettingsSlice";
import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import useLayoutPresets from "@/components/AppHeader/hooks/useLayoutPresets";
import Icon, { ICONS } from "@/components/Core/Icons";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import useLayoutStore, { LayoutMode } from "@/store/zustand/layoutStore";
import GenerateInput from "../AI/GenerateInput";
import useToggleDriveMode from "../AppHeader/hooks/useToggleDriveMode";
import AnimatedToggle from "../ui/animated-toggle";
import GenerateImageInput from "../AI/GenerateImageInput";
import { InteractDesignToggle } from "./components/InteractDesignToggle";

function DesignToolsPicker() {
  const { options: layoutOptions } = useLayoutPresets();
  const dispatch = useDispatch();
  const showModal = useSelector((state: any) => state.globalSettings.showModal);

  const layoutMode = useLayoutStore((state) => state.layoutMode);

  const selectedLayoutOption = useSelector(
    (state: any) => state.globalSettings.selectedLayoutOption
  );

  const selectedLayoutOptionToUse = useMemo(() => {
    return selectedLayoutOption || layoutOptions[0];
  }, [selectedLayoutOption, layoutOptions]);

  const isSpacePressed = useSelector(
    (state: any) => state.globalSettings.isSpacePressed
  );

  const cursorType = useSelector(
    (state: any) => state.globalSettings.cursorType
  );

  const modalActive = showModal !== ModalType.None;

  const cursorTypeToUse = isSpacePressed ? CursorType.Pan : cursorType;

  const addContextMode = useLayoutStore((state) => state.addContextMode);

  const { toggleCursor } = useToggleDriveMode();

  const LayoutIcon = useMemo(() => {
    return selectedLayoutOptionToUse.icon;
  }, [selectedLayoutOptionToUse]);

  return (
    <nav className="flex flex-col gap-1 min-w-[308px]">
      {showModal === ModalType.AI && (
        <GenerateInput
          addContextMode={layoutMode === LayoutMode.PRD && addContextMode}
        />
      )}
      <div
        className={cn(
          "min-h-10 w-full justify-between flex items-center px-2 py-2 rounded-lg mx-auto gap-1 pointer-events-auto transition-all bg-background-primary",
          isSpacePressed && "pointer-events-none cursor-grab",
          showModal === ModalType.Image && "bg-transparent"
        )}
      >
        {showModal === ModalType.Image ? (
          <GenerateImageInput />
        ) : (
          <>
            <Button
              size="icon"
              variant={showModal === ModalType.AI ? "primary" : "ghost"}
              data-tooltip-id="tooltip"
              data-tooltip-content={"AI"}
              data-tooltip-hidden={
                showModal === ModalType.AI || layoutMode === LayoutMode.Code
              }
              data-mouse-down-id="design-tool-button"
              className="group px-1.5 w-auto h-8 rounded-md"
              onClick={() => {
                if (showModal === ModalType.AI) {
                  dispatch(setModal(ModalType.None));
                } else {
                  dispatch(setModal(ModalType.AI));
                  dispatch(
                    setCursorType({
                      cursorType: CursorType.Pointer,
                    })
                  );
                }
              }}
            >
              <Icon
                icon={ICONS.AI_LOGO}
                width={18}
                height={18}
                className={cn(
                  "transition-colors opacity-20 group-hover:opacity-100",
                  {
                    "opacity-100": showModal === ModalType.AI,
                  }
                )}
              />
            </Button>
            <div className="w-[1px] h-6 bg-secondary-n-900" />
            <Button
              size="icon"
              variant={
                !modalActive && cursorTypeToUse === CursorType.Pointer
                  ? "primary"
                  : "ghost"
              }
              data-tooltip-id="tooltip"
              data-tooltip-content="Select"
              data-mouse-down-id="design-tool-button"
              className={cn("px-1.5 w-auto h-8 text-icons-muted rounded-md", {
                "text-foreground":
                  !modalActive && cursorTypeToUse === CursorType.Pointer,
              })}
              onClick={() => {
                dispatch(setModal(ModalType.None));
                dispatch(
                  setCursorType({
                    cursorType: CursorType.Pointer,
                  })
                );
              }}
            >
              <SquareMousePointer className="h-5 w-5" />
            </Button>
            <Button
              size="icon"
              variant={
                (!modalActive && cursorTypeToUse === CursorType.Draw) ||
                showModal === ModalType.Frame
                  ? "primary"
                  : "ghost"
              }
              data-tooltip-id="tooltip"
              data-tooltip-content="Draw"
              data-mouse-down-id="design-tool-button"
              className={cn("px-1.5 w-8 h-8 text-icons-muted rounded-md", {
                "text-foreground":
                  (!modalActive && cursorTypeToUse === CursorType.Draw) ||
                  showModal === ModalType.Frame,
              })}
              onClick={() => {
                dispatch(setModal(ModalType.None));
                dispatch(
                  setCursorType({
                    cursorType: CursorType.Draw,
                  })
                );
              }}
            >
              <LayoutIcon className="w-4 h-4" strokeWidth={2.5} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger
                onKeyDown={(e) => e.preventDefault()}
                asChild
              >
                <Button
                  size="icon"
                  variant="ghost"
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Drawing options"
                  className="px-0 w-auto h-8 text-icons-muted rounded-md"
                >
                  <ChevronDown className="w-3 h-3 transition-all duration-300 ease-in-out" />
                </Button>
              </DropdownMenuTrigger>
              <FrameOptions />
            </DropdownMenu>
            <Button
              size="icon"
              variant={
                (!modalActive && cursorTypeToUse === CursorType.Text) ||
                showModal === ModalType.Text
                  ? "primary"
                  : "ghost"
              }
              data-tooltip-id="tooltip"
              data-tooltip-content="Insert"
              data-mouse-down-id="design-tool-button"
              className={cn("px-1.5 w-8 h-8 text-icons-muted rounded-md", {
                "text-foreground":
                  (!modalActive && cursorTypeToUse === CursorType.Text) ||
                  showModal === ModalType.Text,
              })}
              onClick={() => {
                dispatch(
                  setCursorType({
                    cursorType: CursorType.Text,
                  })
                );
              }}
            >
              <TypeIcon className="w-4 h-4" strokeWidth={2.5} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger
                onKeyDown={(e) => e.preventDefault()}
                asChild
              >
                <Button
                  size="icon"
                  variant="ghost"
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Insert Options"
                  className="p-0 w-auto h-8 text-icons-muted rounded-md"
                >
                  <ChevronDown className="w-3 h-3 transition-all duration-300 ease-in-out" />
                </Button>
              </DropdownMenuTrigger>
              <TypographyOptions />
            </DropdownMenu>
            <Button
              variant={
                !modalActive && cursorTypeToUse === CursorType.Pan
                  ? "primary"
                  : "ghost"
              }
              size="icon"
              data-tooltip-id="tooltip"
              data-tooltip-content="Pan (Space)"
              data-mouse-down-id="design-tool-button"
              className={cn("px-1.5 w-auto h-8 text-icons-muted rounded-md", {
                "text-foreground":
                  !modalActive && cursorTypeToUse === CursorType.Pan,
              })}
              onClick={() => {
                dispatch(setModal(ModalType.None));
                dispatch(
                  setCursorType({
                    cursorType: CursorType.Pan,
                  })
                );
              }}
            >
              <Hand className="w-5 h-5" />
            </Button>
            <Button
              variant={showModal === ModalType.Image ? "primary" : "ghost"}
              size="icon"
              data-tooltip-id="tooltip"
              data-tooltip-content="Generate Image"
              data-mouse-down-id="design-tool-button"
              className={cn("px-1.5 w-auto h-8 text-icons-muted rounded-md", {
                "text-foreground": showModal === ModalType.Image,
              })}
              onClick={() => {
                if (showModal === ModalType.Image) {
                  dispatch(setModal(ModalType.None));
                } else {
                  dispatch(setModal(ModalType.Image));
                  dispatch(
                    setCursorType({
                      cursorType: CursorType.Pointer,
                    })
                  );
                }
              }}
            >
              <ImageIcon className="w-5 h-5" />
            </Button>
            <div className="w-[1px] h-6 bg-secondary-n-900" />
            <InteractDesignToggle />
          </>
        )}
      </div>
    </nav>
  );
}

export default React.memo(DesignToolsPicker);
